import { z } from "zod";

import { AWAKE_HOUR_PACKS } from "./billing-remote-agent.schemas.ts";
import { billingPeriodSchema, creditPackKindSchema, OFFERED_PLANS } from "./billing.schemas.ts";
import { paymentProviderSchema } from "./payment-providers.schemas.ts";

// ── Checkout, portal and the webhook (S5.06) ──────────────────────────────

// A checkout naming no provider goes to the first that is on and can pay for it (ADR-017 §5). A plan
// checkout is a new purchase, so it names only a plan on sale (D1322); a subscriber's renewals never
// pass through here.
export const startCheckoutInputSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("plan"),
    plan: z.enum(OFFERED_PLANS),
    period: billingPeriodSchema,
    provider: paymentProviderSchema.optional(),
  }),
  z.object({
    kind: z.literal("credits"),
    creditKind: creditPackKindSchema,
    credits: z.number().int().min(1_000, "Minimum: 1000 credits"),
    provider: paymentProviderSchema.optional(),
  }),
  // An awake-hours pack (ADR-023 §13), only one of the listed sizes; it needs the add-on.
  z.object({
    kind: z.literal("hours"),
    hours: z
      .number()
      .int()
      .refine((hours) => AWAKE_HOUR_PACKS.some((pack) => pack.hours === hours), "Choose a listed pack."),
    provider: paymentProviderSchema.optional(),
  }),
]);
export type StartCheckoutInput = z.infer<typeof startCheckoutInputSchema>;

export const checkoutSessionSchema = z.object({ id: z.string(), url: z.url() });
export type CheckoutSession = z.infer<typeof checkoutSessionSchema>;

export const portalSessionSchema = z.object({ url: z.url() });
export type PortalSession = z.infer<typeof portalSessionSchema>;

export const webhookResultSchema = z.object({ handled: z.boolean() });
export type WebhookResult = z.infer<typeof webhookResultSchema>;

/** What a payment provider's webhook door (`POST /api/billing/webhook/<provider>`, ADR-017 §7)
 * reads off the wire: the raw body, verbatim. Its proof (Stripe's `Stripe-Signature`, Swiss
 * Bitcoin Pay's header billing set) comes separately, off the request itself, and only the
 * service looks inside either. */
export const webhookRestInputSchema = z.object({ body: z.string() });
export type WebhookRestInput = z.infer<typeof webhookRestInputSchema>;
